import { chatSidebarOpenAtom } from '@/atoms/chatSidebarAtoms';
import { externalLaunchContextAtom } from '@/atoms/externalLaunchAuthAtoms';
import { epicLaunchSessionIdAtom } from '@/atoms/integrations/integrationAtoms';
import { enqueueModalAtom, seenModalIdsAtom } from '@/atoms/modalQueueAtoms';
import {
  setSidebarOpenTab,
  sidebarOpenTabAtom,
  SidebarTab,
} from '@/atoms/persistedPreferencesAtoms';
import { ScribeSpotlight } from '@/components/chat/components/ScribeSpotlightTooltip';
import { IS_SIDEBAR_PANEL_ENABLED } from '@/components/constants/flags';
import { MenuCoachmarkHoverStep } from '@/components/layout/Navigation/coachmark';
import { useDictationSidebarSpotlightPorts } from '@/composition/hosts/dictation-sidebar-spotlight-ports';
import { useIsNewSessionListEnabled } from '@/features/flags';
import {
  requestSessionsSplitAtom,
  sessionListCanFitSplitAtom,
  sessionListCollapsedAtom,
  sessionListSheetOpenAtom,
} from '@/features/sessions/context/sessions-panel-atoms';
import { useIsEhrLaunch } from '@/hooks/integrations/useIsEhrLaunch';
import { useMutateUpdateUser } from '@/hooks/mutation/useMutateUpdateUser';
import {
  getFirstCachedSessionId,
  invalidateSessionsQuery,
} from '@/hooks/query/useQueryCombinedSessions';
import { useQueryUser } from '@/hooks/query/useQueryUser';
import { useTutorial } from '@/hooks/useTutorial';
import { useIsMobile } from '@/hooks/useWindowDimensions';
import { ModalPriority } from '@/types/modal-queue.types';
import { DictateScribeCrossSellSpotlight } from '@heidi/dictate';
import { useTranslations } from '@heidi/runtime/i18n';
import { useCurrentSessionId } from '@heidi/runtime/shell';
import { ModalId, SignupSource, TemplatePages } from '@heidi/schema/ml-scribe';
import { useAtomValue, useSetAtom } from 'jotai';
import { AudioLines, ChevronLeft, ChevronRight } from 'lucide-react';
import { useRouter } from 'next/router';
import { type MouseEvent, useCallback } from 'react';
import { NavItem } from '..';
import { SessionsPanelAction, SessionsPanelContent } from '../../mobile/panels';
import { useMobilePanel } from '../../mobile/use-mobile-panel';
import { OpenSessionListTooltip } from './OpenSessionListTooltip';
import { useCachedRestoreTargetSessionId } from './use-cached-restore-target-session-id';

const SCRIBE_ACTIVE_HREFS = ['/scribe/session', '/scribe/sessions'] as const;

// Extracted from the component body to keep its cognitive complexity within
// budget. Pure function of the four flags that decide the nav-item chevron.
const getScribeNavBadge = ({
  isMobile,
  isSidebarPanelEnabled,
  isNewSessionListEnabled,
  isSidebarSessionListOpen,
}: {
  isMobile: boolean;
  isSidebarPanelEnabled: boolean;
  isNewSessionListEnabled: boolean;
  isSidebarSessionListOpen: boolean;
}) => {
  // Mobile: always show a chevron-right for panel access.
  if (isMobile) return <ChevronRight className="size-4 rtl:-scale-x-100" />;
  // Desktop: only legacy mode (neither new session list nor SidebarPanel) shows
  // a toggle chevron.
  if (isSidebarPanelEnabled || isNewSessionListEnabled) return undefined;
  return isSidebarSessionListOpen ? (
    <ChevronLeft className="size-4 rtl:-scale-x-100" />
  ) : (
    <ChevronRight className="size-4 rtl:-scale-x-100" />
  );
};

export const ScribeNavItem = () => {
  const { t } = useTranslations();
  const router = useRouter();
  const isMobile = useIsMobile();
  const { openPanel, dismissMobileSidebar } = useMobilePanel();
  const { isOnDictationLogsRoute, onStartScribeSession } =
    useDictationSidebarSpotlightPorts();
  const setChatSidebarOpen = useSetAtom(chatSidebarOpenAtom);
  const { mutate: updateUser } = useMutateUpdateUser();
  const { data: shouldShowTryScribeTooltip } = useQueryUser(
    (data) => data?.should_show_try_scribe_tooltip
  );
  const { data: signupSource } = useQueryUser((data) => data?.signup_source);
  const seenModalIds = useAtomValue(seenModalIdsAtom);
  const enqueueModal = useSetAtom(enqueueModalAtom);

  // Falsy seenModalIds = not loaded yet.
  const shouldShowOnboardingModal =
    signupSource === SignupSource.dictate &&
    !!seenModalIds &&
    !seenModalIds.has(ModalId.SCRIBE_ONBOARDING_DICTATE);

  const prefetchOnboardingModal = useCallback(() => {
    if (shouldShowOnboardingModal) {
      void import('@/components/modal/scribe-onboarding-dictate-modal');
    }
  }, [shouldShowOnboardingModal]);

  // Check if new SidebarPanel is enabled (replaces old session list toggle)
  const isSidebarPanelEnabled = IS_SIDEBAR_PANEL_ENABLED();
  const isNewSessionListEnabled = useIsNewSessionListEnabled();

  // Get state values directly
  const externalLaunchContext = useAtomValue(externalLaunchContextAtom);
  const enableSessionList = !useIsEhrLaunch();
  const epicLaunchSessionId = useAtomValue(epicLaunchSessionIdAtom);
  const ehrLaunchSessionId =
    epicLaunchSessionId || externalLaunchContext?.sessionId || '';
  const sidebarOpenTab = useAtomValue(sidebarOpenTabAtom);
  const isSidebarSessionListOpen = sidebarOpenTab === SidebarTab.SESSION_LIST;
  const setRequestSessionsSplit = useSetAtom(requestSessionsSplitAtom);
  const setSessionListSheetOpen = useSetAtom(sessionListSheetOpenAtom);
  const isSessionListCollapsed = useAtomValue(sessionListCollapsedAtom);
  const isSessionListCanFitSplit = useAtomValue(sessionListCanFitSplitAtom);

  // Default to split view when entering the new session list from the sidebar.
  // When already viewing a session, target that same session so the click keeps
  // the current patient and just re-opens the list (the nonce bump below drives
  // the actual split). Otherwise open the session the list's own restore would
  // select from its already-cached rows, then the legacy cached session —
  // mirroring the legacy `/scribe/session` index redirect. Both reads are
  // cache-only (no subscription) so this always-mounted nav doesn't poll the
  // sessions endpoint app-wide, and both fall back to the bare list, which
  // restores the session itself once its own query resolves.
  const currentSessionId = useCurrentSessionId();
  // `getFirstCachedSessionId` knows only the legacy V1/V2/V3 list caches, which
  // the new list runs alongside — so a v2.2 miss can still open a session off a
  // legacy hit. Where both miss, the click lands on the bare route and waits
  // for restore to redirect before the session starts loading.
  const cachedRestoreSessionId = useCachedRestoreTargetSessionId();
  const onSessionsRoute = router.pathname.startsWith('/scribe/sessions');
  // On the sessions route with no session selected, the list is enlarged
  // fullscreen by the user's choice — clicking Scribe must be a no-op (handled
  // in `handleScribeClick`), so it doesn't yank them back into split view.
  const isOnFullscreenList = onSessionsRoute && !currentSessionId;
  const splitTargetSessionId = isOnFullscreenList
    ? '' // keep the href on the bare list; the click is a no-op anyway
    : (onSessionsRoute && currentSessionId) ||
      cachedRestoreSessionId ||
      getFirstCachedSessionId();

  const scribeHref =
    !isMobile && isNewSessionListEnabled
      ? splitTargetSessionId
        ? `/scribe/sessions/${splitTargetSessionId}`
        : '/scribe/sessions'
      : !isMobile && isSidebarPanelEnabled
        ? '/scribe'
        : undefined;
  // Steady-state only: NavItem infers the pending-nav highlight from
  // activeMatch/activeHrefs. This value feeds NavItem's isActive and the
  // open-list tooltip below.
  const isScribeActive = router.asPath.startsWith('/scribe/session');
  const { tutorialStepCurrentPage, panelOpen } = useTutorial();
  const shouldHighlightExpandSession =
    tutorialStepCurrentPage?.enum === TemplatePages.NAVIGATE_BACK_TO_SESSION && panelOpen;

  // `canFitSplit` gate: in single-pane mode the click can't open the list.
  // Also covers mobile (viewport < 640 → container < 840 → canFitSplit=false).
  const shouldShowOpenListTooltip =
    isNewSessionListEnabled &&
    isScribeActive &&
    isSessionListCollapsed &&
    isSessionListCanFitSplit;

  const handleScribeClick = useCallback(
    (event?: MouseEvent) => {
      if (shouldShowOnboardingModal) {
        enqueueModal({
          id: ModalId.SCRIBE_ONBOARDING_DICTATE,
          priority: ModalPriority.NORMAL,
          dismissible: true,
          showOnce: true,
        });
      }

      if (isMobile) {
        if (isNewSessionListEnabled) {
          // On mobile (the hamburger-nav breakpoint, < 640px), Scribe always
          // returns to the full session list rather than leaving the user on the
          // session/page they were viewing.
          void router.push('/scribe/sessions');
          dismissMobileSidebar();
          void invalidateSessionsQuery();
          return;
        }
        openPanel(<SessionsPanelContent />, <SessionsPanelAction />);
        return;
      }

      if (isNewSessionListEnabled) {
        // Deliberately on the enlarged fullscreen list (no session open):
        // clicking Scribe does nothing. The user re-enters split by selecting a
        // session — we must not re-open the top session or re-collapse the list.
        if (isOnFullscreenList) {
          event?.preventDefault();
          return;
        }
        // New session list page: navigate via href, just close other sidebars
        setSidebarOpenTab(undefined);
        setChatSidebarOpen(false);
        void invalidateSessionsQuery();
        // Single-pane (narrow / split-screen): the split can't be re-opened in
        // place — no list panel is mounted beside the detail, so the nonce bump
        // below would be a no-op and the href is the current session (a same-URL
        // navigation). Open the list as a left side sheet over the current
        // session instead, so Scribe reveals the list without leaving the
        // session the user is in. The sheet mount lives on the sessions route in
        // exactly this state, so setting the atom is enough to reveal it.
        if (onSessionsRoute && currentSessionId && !isSessionListCanFitSplit) {
          event?.preventDefault();
          setSessionListSheetOpen(true);
        } else {
          // Force the split back open. Handles the in-place case: already on a
          // collapsed full-screen detail, where the href is a no-op navigation and
          // wouldn't otherwise re-expand the list.
          setRequestSessionsSplit((nonce) => nonce + 1);
          // Already viewing this session: skip the same-URL navigation so the loaded
          // detail isn't reloaded — the nonce bump above reveals the list in place.
          if (onSessionsRoute && currentSessionId) {
            event?.preventDefault();
          }
        }
      } else if (isSidebarPanelEnabled) {
        // New panel mode: clear legacy Redux state; panel handled by hover/pin system
        setSidebarOpenTab(undefined);
        setChatSidebarOpen(false);

        // We are invalidating the sessions query here because we are routing to the scribe page.
        void invalidateSessionsQuery();
      } else {
        setSidebarOpenTab(isSidebarSessionListOpen ? undefined : SidebarTab.SESSION_LIST);
        if (!isSidebarSessionListOpen) {
          setChatSidebarOpen(false);
          void invalidateSessionsQuery();
        }
      }

      if (shouldShowTryScribeTooltip) {
        updateUser({ should_show_try_scribe_tooltip: false });
      }
    },
    [
      isMobile,
      isNewSessionListEnabled,
      isSidebarPanelEnabled,
      isSidebarSessionListOpen,
      openPanel,
      setChatSidebarOpen,
      updateUser,
      shouldShowTryScribeTooltip,
      dismissMobileSidebar,
      setRequestSessionsSplit,
      setSessionListSheetOpen,
      onSessionsRoute,
      currentSessionId,
      isOnFullscreenList,
      isSessionListCanFitSplit,
      router,
      shouldShowOnboardingModal,
      enqueueModal,
    ]
  );

  return (
    <>
      {enableSessionList && (
        <MenuCoachmarkHoverStep>
          <ScribeSpotlight
            onTryNow={handleScribeClick}
            enableSessionList={enableSessionList}
          >
            <DictateScribeCrossSellSpotlight
              isOnDictationLogsRoute={isOnDictationLogsRoute}
              onStartScribeSession={onStartScribeSession}
            >
              <OpenSessionListTooltip enabled={shouldShowOpenListTooltip}>
                <NavItem
                  label={t('sidenav.scribe')}
                  icon={<AudioLines />}
                  // Desktop navigates via this href (Link); mobile leaves it
                  // undefined and navigates imperatively in handleScribeClick
                  // (router.push to the session list, or the legacy panel).
                  href={scribeHref}
                  isActive={isScribeActive}
                  activeMatch="exact"
                  activeHrefs={SCRIBE_ACTIVE_HREFS}
                  isHighlighted={
                    sidebarOpenTab !== SidebarTab.SESSION_LIST &&
                    shouldHighlightExpandSession
                  }
                  onClick={handleScribeClick}
                  onMouseEnter={prefetchOnboardingModal}
                  testId="sidebar-link-view-sessions"
                  sidebarPanelType={isNewSessionListEnabled ? undefined : 'sessions'}
                  badge={getScribeNavBadge({
                    isMobile,
                    isSidebarPanelEnabled,
                    isNewSessionListEnabled,
                    isSidebarSessionListOpen,
                  })}
                />
              </OpenSessionListTooltip>
            </DictateScribeCrossSellSpotlight>
          </ScribeSpotlight>
        </MenuCoachmarkHoverStep>
      )}
      {!enableSessionList && !!ehrLaunchSessionId && (
        <NavItem
          label={t('sidenav.session')}
          icon={<AudioLines />}
          onClick={() => {
            void router.push({
              pathname: `/scribe/session/${ehrLaunchSessionId}`,
            });
            dismissMobileSidebar();
          }}
          testId="sidebar-link-open-last-session"
        />
      )}
    </>
  );
};
